import { useState, useRef, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EmojiPicker from 'emoji-picker-react';
import { useDispatch, useSelector } from 'react-redux';
import { getContainerClass } from "../../components/navigation/navigation";
import { fetchConversations, markConversationRead, setSelectedConversation, updateLastMessage } from '../../redux/slices/conversationSlice';
import { fetchMessages, sendMessage, clearMessages, addMessage } from '../../redux/slices/messageSlice';
import { socketActions } from '../../redux/middleware/socketMiddleware';
import storage from '../../utils/storage';
import './MessagesPage.css';
import { useLocation } from "react-router-dom";
import Cover from "../../components/cover/cover";
import { WifiOff } from 'lucide-react';
import { createConversation } from '../../services/api';

const MessagesPage = () => {
  const dispatch = useDispatch();
  const messagesListRef = useRef(null);
  const [newMessage, setNewMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [disable, setDisable] = useState(false);
  const location = useLocation();
  // Redux state
  const { conversations, selectedConversation, selectedUserName, loading } = useSelector(state => state.conversations);
  const { messages, loading: loadingMessages } = useSelector(state => state.messages);
  const [conversationsFetched, setConversationsFetched] = useState(false);
  const currentUser = useSelector(state => state.auth.user);
  const [userId, setUserId] = useState(currentUser?._id);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Get userId from storage if not available from Redux
  useEffect(() => {
    const getUserId = async () => {
      if (!currentUser?._id) {
        try {
          const storedUserId = await storage.getItem('userId');
          setUserId(storedUserId);
        } catch (error) {
          console.error('Error getting userId from storage:', error);
        }
      }
    };
    getUserId();
  }, [currentUser?._id]);


  const placeholderMessages = [
    {
      id: 'offline-msg-1',
      isMe: true,
      sender: 'Me',
      messages: [{
        id: 'offline-msg-1-1',
        text: 'Previous messages will appear when you\'re back online',
        time: 'Offline'
      }]
    }
  ];

  // Ensure socket is connected when component mounts (socket is global, managed by middleware)
  useEffect(() => {
    if (userId) {
      console.log('MessagesPage: Ensuring socket connection for userId:', userId);
      // Just ensure connection - don't disconnect on unmount as socket is global
      dispatch(socketActions.connect(userId));
    }
  }, [userId, dispatch]);

  // Clear session storage and states on component mount
  useEffect(() => {
    // console.log('MessagesPage: Clearing session storage and messages');
    localStorage.removeItem('selectedConversationId');
    localStorage.removeItem('selectedConversation');
    dispatch(clearMessages());
  }, [dispatch]);

  // Modify the useEffect for initial data fetch
  useEffect(() => {
    if (isOnline) {
      dispatch(fetchConversations()).finally(() => setConversationsFetched(true));

      const refreshInterval = setInterval(() => {
        if (isOnline) dispatch(fetchConversations());
      }, 30000);

      return () => clearInterval(refreshInterval);
    }
  }, [dispatch, isOnline]);

  // After conversations load, check if we need to preselect by name
  useEffect(() => {
    const targetId = location.state?.preselectUserId;
    const targetName = location.state?.preselectUserName;

    if (!targetId || !conversationsFetched || selectedConversation) return;

    // 1. Try to find existing conversation
    const existing = conversations.find(c =>
      c.members?.some(m => (typeof m === 'string' ? m : m?._id) === targetId) ||
      c.userId === targetId ||
      c.otherUserId === targetId
    );

    if (existing) {
      handleConversationClick(existing.id || existing._id);
    } else {
      createNewConversation(targetId, targetName);
    }
  }, [location.state, conversations, selectedConversation, conversationsFetched]);

  const createNewConversation = async (receiverId, receiverName) => {
    try {
      const response = await createConversation({ receiverID: receiverId });

      if (response.data.status) {
        // Backend returns the conversation (either newly created or already existing)
        const newConvo = response.data.conversation;
        const newConvoId = newConvo._id || newConvo;   // handle both cases

        // Refresh the list
        await dispatch(fetchConversations());

        // Select the conversation
        dispatch(setSelectedConversation({
          conversationId: newConvoId,
          userName: receiverName
        }));

        // Load messages (will be empty if brand new)
        dispatch(fetchMessages(newConvoId));
      } else {
        toast.error(response.data.message || 'Failed to start conversation');
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Failed to start conversation');
    }
  };

  // Re-fetch conversations periodically (socket connection is managed globally)
  useEffect(() => {
    if (userId) {
      // console.log('MessagesPage: Fetching conversations');
      dispatch(fetchConversations());
    }
  }, [userId, dispatch]);

  // Scroll to bottom whenever messages change or conversation is selected
  useEffect(() => {
    scrollToBottom();
  }, [messages, selectedConversation]);

  // Add online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (userId) {
        // Reconnect socket if needed (middleware handles reconnection logic)
        dispatch(socketActions.connect(userId));
      }
      dispatch(fetchConversations());
    };

    const handleOffline = () => {
      setIsOnline(false);
      // Don't disconnect socket on offline - let it handle reconnection automatically
      // Socket middleware has built-in reconnection logic
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [userId, dispatch]);

  const scrollToBottom = () => {
    if (messagesListRef.current) {
      try {
        // Try smooth scrolling first
        messagesListRef.current.scrollTo({
          top: messagesListRef.current.scrollHeight,
          behavior: 'smooth'
        });
      } catch (e) {
        // Fallback for older browsers
        messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
      }
    }
  };

  // Handle conversation selection
  const handleConversationClick = async (conversationId) => {
    console.log('MessagesPage: Selecting conversation:', conversationId);
    const selectedConvo = conversations.find(c => c.id === conversationId);
    if (selectedConvo) {
      dispatch(setSelectedConversation({
        conversationId,
        userName: selectedConvo.name
      }));
      dispatch(clearMessages());
      dispatch(fetchMessages(conversationId));
      dispatch(markConversationRead(conversationId));
      scrollToBottom(); // Scroll after messages are loaded
    }
  };

  // Handle sending messages
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || disable) {
      return;
    }

    if (!userId) {
      toast.error('Please log in to send messages');
      return;
    }

    dispatch(socketActions.connect(userId));

    setDisable(true);
    const messageText = newMessage.trim();
    setNewMessage('');

    try {
      const selectedConvo = conversations.find(
        (c) => c.id === selectedConversation || c._id === selectedConversation
      );

      let receiver = null;

      if (selectedConvo) {
        receiver = selectedConvo.members?.find((m) => {
          const memberId = typeof m === 'object' && m !== null ? m._id : m;
          return memberId && String(memberId) !== String(userId);
        });
      }

      if (!receiver && location.state?.preselectUserId) {
        receiver = location.state.preselectUserId;
      }

      if (!receiver) {
        throw new Error('Receiver not found');
      }

      // ★ Define BEFORE use
      const receiverId =
        typeof receiver === 'object' && receiver !== null
          ? receiver._id
          : receiver;

      dispatch(
        addMessage({
          id: Date.now(),
          sender: 'Me',
          text: messageText,
          time: new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          isMe: true,
          conversationId: selectedConversation,
          senderId: userId,
        })
      );

      dispatch(
        updateLastMessage({
          conversationId: selectedConversation,
          message: messageText,
          time: new Date().toISOString(),
          senderId: userId,
          senderName: 'Me',
          isFromCurrentUser: true,
        })
      );

      await dispatch(
        sendMessage({
          conversationId: selectedConversation,
          text: messageText,
          receiver: receiverId,
        })
      ).unwrap();

      const senderName =
        currentUser?.firstName ||
        (await storage.getItem('firstName')) ||
        'Me';

      dispatch(
        socketActions.sendMessage({
          senderId: userId,
          recieverId: receiverId,
          text: messageText,
          senderName,
          conversationId: selectedConversation,
        })
      );

      scrollToBottom();
    } catch (error) {
      console.error('MessagesPage: Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
      setNewMessage(messageText);
    } finally {
      setDisable(false);
    }
  };

  // Add emoji handler
  const onEmojiClick = (emojiObject) => {
    setNewMessage(prevMessage => prevMessage + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  // Add click-away handler for emoji picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && !event.target.closest('.emoji-picker-container')) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Update the offline banner component
  const OfflineBanner = () => (
    <div className="offline-banner">
      <WifiOff size={16} />
      <span>You're offline. Messages will be available when you reconnect.</span>
    </div>
  );

  // Update BlankStateMessage to handle offline state
  const BlankStateMessage = () => (
    <div className="no-conversation-selected">
      <div className="blank-state-content">
        <svg
          width="80"
          height="80"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginBottom: '20px', color: '#6c757d' }}
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        <h3 style={{ color: '#343a40', marginBottom: '10px' }}>
          {isOnline ? 'Welcome to Messages' : 'No Messages Available'}
        </h3>
        <p style={{ color: '#6c757d', textAlign: 'center', maxWidth: '300px', margin: '0 auto' }}>
          {isOnline
            ? 'Select a conversation from the left sidebar to start chatting with your connections'
            : 'Messages will be available when you reconnect to the internet'}
        </p>
      </div>
    </div>
  );

  // Modify loading and error handling
  if (loading && isOnline) {
    return (
      <div className={getContainerClass(location.pathname)}>
        <div className="messages-content">
          <div className="loading-message">
            <CircularProgress style={{ color: '#28a745' }} />
          </div>
        </div>
      </div>
    );
  }

  // Remove the error return and handle it within the UI
  const displayConversations = isOnline ? conversations : [];
  const displayMessages = isOnline ? messages : placeholderMessages;

  const breadcrumb = ["Home", "Messages"];

  return (
    <>
      <div className={getContainerClass(location.pathname)}>
        <Cover breadcrumb={breadcrumb} page="messages" />
        <div className="messages-content">
          <Container fluid>
            <Row>
              <Col>
                <motion.div
                  className="messages-container"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {!isOnline && <OfflineBanner />}
                  <div className="conversations-sidebar">
                    <div className="conversations-header">
                      <h5>
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginRight: '8px', verticalAlign: 'middle' }}
                        >
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                        Messages
                      </h5>
                    </div>
                    <div className="conversations-list">
                      {displayConversations.length > 0 ? (
                        displayConversations.map(conversation => (
                          <div
                            key={conversation.id}
                            className={`conversation-item ${selectedConversation === conversation.id ? 'selected' : ''}`}
                            onClick={() => handleConversationClick(conversation.id)}
                          >
                            <div className="conversation-avatar">
                              {conversation.userData?.profilePic ? (
                                <img
                                  src={conversation.userData.profilePic}
                                  alt={conversation.name}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : null}
                              <div
                                style={{
                                  display: conversation.userData?.profilePic ? 'none' : 'flex',
                                  width: '100%',
                                  height: '100%',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '1.2rem',
                                  color: 'white'
                                }}
                              >
                                {conversation.name.charAt(0)}
                              </div>
                            </div>
                            <div className="conversation-details">
                              <div className="conversation-header">
                                <span className="conversation-name">{conversation.name}</span>
                                <span className="conversation-time">{conversation.time}</span>
                                {conversation.unread > 0 && (
                                  <div className="unread-badge">
                                    {conversation.unread}
                                  </div>
                                )}
                              </div>
                              <div className="conversation-last-message">
                                {conversation.lastMessage}
                              </div>
                            </div>

                          </div>
                        ))
                      ) : (
                        <div className="no-messages-state">
                          <p>No messages to display</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="chat-container">
                    {selectedConversation ? (
                      <>
                        <div className="messages-header">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {selectedConversation && (() => {
                              const selectedConvo = conversations.find(c => c.id === selectedConversation);
                              return selectedConvo?.userData?.profilePic ? (
                                <img
                                  src={selectedConvo.userData.profilePic}
                                  alt={selectedUserName}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                  style={{
                                    width: '35px',
                                    height: '35px',
                                    borderRadius: '50%',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : null;
                            })()}
                            <div
                              style={{
                                display: (() => {
                                  const selectedConvo = conversations.find(c => c.id === selectedConversation);
                                  return selectedConvo?.userData?.profilePic ? 'none' : 'flex';
                                })(),
                                width: '35px',
                                height: '35px',
                                borderRadius: '50%',
                                backgroundColor: '#28a745',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.2rem',
                                color: 'white'
                              }}
                            >
                              {selectedUserName?.charAt(0) || 'C'}
                            </div>
                            <h6 style={{ margin: 0 }}>{selectedUserName || 'Chat'}</h6>
                          </div>
                        </div>

                        <div className="messages-list" ref={messagesListRef}>
                          {loadingMessages && isOnline ? (
                            <div style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: '200px'
                            }}>
                              <CircularProgress style={{ color: '#28a745' }} />
                            </div>
                          ) : (
                            displayMessages.map((group, groupIndex) => {
                              const isCurrentUser = group.isMe || group.sender === 'Unknown';
                              return (
                                <div
                                  key={groupIndex}
                                  className={`message-group ${isCurrentUser ? 'message-group-right' : 'message-group-left'}`}
                                >
                                  {!isCurrentUser && <div className="message-sender">{group.sender}</div>}
                                  {group.messages.map((message, messageIndex) => (
                                    <div
                                      key={message.id}
                                      className={`message-bubble ${isCurrentUser ? 'message-bubble-right' : 'message-bubble-left'}`}
                                    >
                                      <div className="message-text">{message.text}</div>
                                      {messageIndex === group.messages.length - 1 && (
                                        <div className="message-time">{message.time}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              );
                            })
                          )}
                        </div>

                        <form onSubmit={handleSendMessage} className={`message-input-container ${!isOnline ? 'disabled' : ''}`}>
                          <div className="emoji-picker-container">
                            {showEmojiPicker && isOnline && (
                              <div className="emoji-picker-wrapper">
                                <EmojiPicker onEmojiClick={onEmojiClick} />
                              </div>
                            )}
                            <button
                              type="button"
                              className="emoji-button"
                              onClick={() => isOnline && setShowEmojiPicker(!showEmojiPicker)}
                              disabled={!isOnline}
                            >
                              😊
                            </button>
                          </div>
                          <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type a message..."
                            className="message-input"
                            disabled={!isOnline || disable}
                          />
                          <button
                            type="button"
                            className="send-button"
                            disabled={!isOnline || disable}
                            onClick={handleSendMessage}
                          >
                            Send
                          </button>
                        </form>
                      </>
                    ) : (
                      <BlankStateMessage />
                    )}
                  </div>
                </motion.div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </>
  );
};

export default MessagesPage; 